import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Loader2 } from 'lucide-react';
import { useProfessionalCategories, searchCategoriesFromData, getCategoryByIdFromData } from '@/hooks/useProfessionalCategories';
import { PROFESSIONAL_CATEGORIES, searchCategories as searchStaticCategories, getCategoryById as getStaticCategoryById } from '@/data/categories';
import { BrutalInput } from './ui/BrutalInput';

interface CategorySelectorProps {
  selectedCategories: string[];
  onChange: (categories: string[]) => void;
  maxCategories?: number;
  disabled?: boolean;
}

export const CategorySelector = ({ 
  selectedCategories, 
  onChange, 
  maxCategories = 3,
  disabled = false
}: CategorySelectorProps) => {
  const { i18n, t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  
  const language = (i18n.language || 'it').substring(0, 2) as 'it' | 'en' | 'es';
  
  // Fetch categories from database
  const { data: dbCategories, isLoading, isError } = useProfessionalCategories(true);
  
  // Use DB categories if available, otherwise fallback to static
  const useStaticFallback = isError || (!isLoading && !dbCategories?.length);
  
  const filteredCategories = useStaticFallback
    ? searchStaticCategories(searchQuery, language, selectedCategories)
    : searchCategoriesFromData(dbCategories || [], searchQuery, language, selectedCategories);
  
  const canAddMore = selectedCategories.length < maxCategories;

  const handleAddCategory = (categoryId: string) => {
    if (canAddMore && !selectedCategories.includes(categoryId)) {
      onChange([...selectedCategories, categoryId]);
      setSearchQuery('');
      setShowDropdown(false);
    }
  };

  const handleRemoveCategory = (categoryId: string) => {
    onChange(selectedCategories.filter(id => id !== categoryId));
  };

  // Get category by ID (from DB or static)
  const getCategory = (catId: string) => {
    if (useStaticFallback) {
      return getStaticCategoryById(catId);
    }
    return getCategoryByIdFromData(dbCategories || [], catId);
  };

  return (
    <div className={disabled ? "space-y-3 pointer-events-none opacity-50" : "space-y-3"}>
      {/* Selected Categories */}
      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-card border-[3px] border-dark">
          {selectedCategories.map(catId => {
            const category = getCategory(catId);
            if (!category) return null;
            return (
              <div
                key={catId}
                className="inline-flex items-center gap-2 bg-primary text-dark px-3 py-2 border-[3px] border-dark font-heading font-bold text-sm"
              >
                <span>{category.icon || ''}</span>
                <span>{category[language]?.name || category.it?.name || catId}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveCategory(catId)}
                  className="hover:text-destructive transition-colors"
                  aria-label={t('common.remove')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search Input */}
      {canAddMore && (
        <div className="relative">
          <div className="relative">
            <BrutalInput
              id="category-search"
              type="text"
              placeholder={t('profile.edit.fields.categoriesPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              className="pr-10"
              disabled={isLoading}
            />
            {isLoading ? (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/60 animate-spin" />
            ) : (
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark/60" />
            )}
          </div>

          {/* Dropdown Results */}
          {showDropdown && !isLoading && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowDropdown(false)}
              />
              
              {/* Dropdown */}
              <div className="absolute z-20 w-full mt-2 bg-card border-[3px] border-dark shadow-[6px_6px_0px_#101322] max-h-[400px] overflow-y-auto">
                {filteredCategories.length === 0 ? (
                  <div className="p-4 text-center text-dark/60 font-body">
                    {t('common.noCategoriesFound')}
                  </div>
                ) : (
                  <div className="p-2">
                    {filteredCategories.map(category => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleAddCategory(category.id)}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-primary hover:text-dark transition-colors text-left font-body font-bold"
                      >
                        <span className="text-2xl">{category.icon || ''}</span>
                        <div className="flex-1">
                          <div className="font-bold">{category[language]?.name || category.it?.name || category.id}</div>
                          <div className="text-xs opacity-70">
                            {(category[language]?.keywords || category.it?.keywords || []).slice(0, 3).join(', ')}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
